import { useTarget } from "lib/@hooks/use-target"
import { useDisabledUntilInvalidation } from "lib/@hooks/use-disabled-until-invalidation"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useDialog } from "lib/@hooks/useDialog"
import { TargetGuard } from "lib/@components/target-guard"
import { EditRecord } from "lib/@components/edit-record"
import { Box, Button, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { rescanMapping } from "routes/asset-register/plugins/asset-mapping/controller/rescan-mapping"
import { updateAssetRegister } from "routes/asset-register/controller/update-asset-register"
import { Bound } from "lib/@components/binding/Bound"
import { useRef } from "react"
import { TreeToolbar } from "slot-definitions"
import noop from "lib/noop"
import { Feature, isEnabled } from "lib/@components/feature"
import { useIsAnythingBeingMapped } from "routes/asset-register/plugins/asset-mapping/use-is-anything-being-mapped"
import { useIsAnythingRefining } from "routes/asset-register/plugins/asset-mapping/use-is-anything-refining"
import { FEATURE_FLAG_REFRESH_ALL_MAPPINGS } from "routes/asset-register/plugins/asset-mapping/feature-flag-refresh-all-mappings.runtime"
import { FEATURE_FLAG_MOBILISER_ADVANCED_MAPPING } from "routes/asset-register/plugins/asset-mapping/feature-flag-mobiliser-advanced-mapping.runtime"
import { createEvent } from "library/local-events/index.mjs"
import {
    getMobiliserStage,
    MOBILISER_STAGE_MAPPING,
    MOBILISER_STAGE_PROCESSING_TYPES,
} from "routes/asset-register/plugins/asset-mapping/mobiliser-stage.mjs"
import { If } from "lib/@components/switch"
import { applyMappingSettings } from "routes/asset-register/plugins/asset-mapping/controller/apply-mapping-settings"
import { getAssetCount } from "routes/asset-register/controller/get-asset-count"
import { warningSnackbar } from "lib/snackbar/warning-snackbar"
import { InnerSettings } from "routes/asset-register/plugins/asset-mapping/inner-settings.js"
import { SETTINGS_DEFAULT } from "routes/asset-register/plugins/asset-mapping/constants.js"
import { confirm } from "lib/@dialogs/confirm"

export const OpenMappingSettings = createEvent("OpenMappingSettings")
const ALREADY_PROCESSING =
    "Mobiliser is already processing this register. Apply your settings again once it has finished."

export function SettingsDialog() {
    const isMapping = useIsAnythingBeingMapped()
    const isRefining = useIsAnythingRefining()
    const currentOptions = useRef()
    const originalOptions = useRef()
    const target = useTarget()
    const { refreshRegister = noop } = useBoundContext()
    const [disabled, disable] = useDisabledUntilInvalidation("typeFromRegister")

    const isMappingStage =
        getMobiliserStage(target?.options, { upgradesEnabled: isEnabled(FEATURE_FLAG_MOBILISER_ADVANCED_MAPPING) }) ===
        MOBILISER_STAGE_MAPPING
    const refreshAllButton = (
        <Feature feature={FEATURE_FLAG_REFRESH_ALL_MAPPINGS}>
            <If value={isMappingStage}>
                <Button
                    disabled={isMapping || isRefining || disabled}
                    size="small"
                    startIcon={<Iconify icon="mdi:refresh" />}
                    onClick={refreshAll}
                    data-cy="refresh-all-mappings-button"
                >
                    Refresh All Mappings
                </Button>
            </If>
        </Feature>
    )

    TreeToolbar.usePlug(refreshAllButton, [isMapping, isRefining, disabled, isMappingStage])

    const showSettings = useDialog(
        <TargetGuard notEmpty>
            <EditRecord field="options" store={storeOptions} initialise debounceTime={500}>
                <Bound
                    saveSettings={saveSettings}
                    originalOptions={originalOptions}
                    haveSettingsChanged={haveSettingsChanged}
                >
                    <InnerSettings />
                </Bound>
            </EditRecord>
        </TargetGuard>,
        { maxWidth: "md" }
    )

    OpenMappingSettings.useEvent(openSettingsDialog)

    return (
        <Box display="flex" justifyContent="flex-end" sx={{ p: 1 }}>
            <Button
                disabled={isMapping || isRefining}
                size="small"
                color="primary"
                startIcon={<Iconify icon="mdi:tune-vertical" />}
                onClick={openSettingsDialog}
                data-cy="mapping-settings-tab"
            >
                Mapping Settings
            </Button>
        </Box>
    )

    async function refreshAll() {
        disable()
        await rescanMapping(target._id, ["all"])
    }

    async function openSettingsDialog() {
        originalOptions.current = structuredClone(target.options)
        const result = await showSettings()

        if (typeof result === "undefined") {
            target.options = originalOptions.current
            currentOptions.current = null
        }
    }

    async function saveSettings(editedOptions) {
        // FIQ-1554: prefer the options handed over by Apply Settings - the debounced
        // EditRecord store may not have flushed into currentOptions yet
        const upgradesEnabled = isEnabled(FEATURE_FLAG_MOBILISER_ADVANCED_MAPPING)
        const registerId = currentOptions.current?.registerId ?? target._id
        const options = editedOptions ?? currentOptions.current?.options ?? target.options
        const nextStage =
            hasTypeSettingsChanged(originalOptions.current, options) ||
            originalOptions.current?.mobiliserStage === MOBILISER_STAGE_PROCESSING_TYPES
                ? MOBILISER_STAGE_PROCESSING_TYPES
                : MOBILISER_STAGE_MAPPING
        const nextOptions = { ...options, hasConfiguredSettings: true }
        if (upgradesEnabled) nextOptions.mobiliserStage = nextStage
        let saved = true

        if (upgradesEnabled && !haveSettingsChanged(originalOptions.current, options)) return true

        try {
            if (!(await hasAssets(registerId))) return false
            if (upgradesEnabled && !(await confirmRemap(options))) return false
            await updateAssetRegister({ registerId, options: nextOptions })
            if (!(await startTypeProcessing(registerId)) && upgradesEnabled) {
                const restored = await updateAssetRegister({
                    registerId,
                    options: { ...nextOptions, ...settingFields(originalOptions.current) },
                })
                if (!restored) console.error("Could not restore the previous mapping settings", registerId)
                warningSnackbar(ALREADY_PROCESSING)
                return false
            }
            currentOptions.current = null
            originalOptions.current = structuredClone(options)
        } catch (e) {
            saved = false
            console.error(e.message)
        }
        if (saved) refreshRegister()
        return saved
    }

    async function storeOptions(options, registerId) {
        currentOptions.current = { options, registerId }
    }

    async function confirmRemap(options) {
        return confirm(buildRemapWarning(originalOptions.current, options), "Re-run mapping with new settings?")
    }

    async function startTypeProcessing(registerId) {
        return applyMappingSettings(registerId)
    }

    async function hasAssets(registerId) {
        const hasAssets = Number(await getAssetCount(registerId)) > 0
        if (!hasAssets) {
            warningSnackbar("Upload assets to this register before starting mapping.")
        }
        return hasAssets
    }

    function settingFields(options) {
        return Object.fromEntries(Object.keys(SETTINGS_DEFAULT).map((field) => [field, settingValue(options, field)]))
    }

    function haveSettingsChanged(before, after) {
        return Object.keys(SETTINGS_DEFAULT).some(hasFieldChanged)

        function hasFieldChanged(field) {
            return settingValue(before, field) !== settingValue(after, field)
        }
    }

    function hasTypeSettingsChanged(before, after) {
        return !!before?.originalTypes !== !!after?.originalTypes
    }

    function settingValue(options, field) {
        return options?.[field] ?? SETTINGS_DEFAULT[field]
    }

    function buildRemapWarning(before, after) {
        const defaultWarning =
            "Existing intelligent mappings and suggestions will be discarded. AI mapping tokens will be consumed."
        const extraWarning = computeExtraWarning(before, after)
        if (!extraWarning) return defaultWarning
        return (
            <>
                <Typography gutterBottom>{defaultWarning}</Typography>
                <Typography>{extraWarning}</Typography>
            </>
        )
    }

    function computeExtraWarning(before, after) {
        if (!before?.originalTypes && after?.originalTypes) {
            return "Switching to Unrefined Types will skip AI asset type suggestions."
        }
        if (before?.originalTypes && !after?.originalTypes) {
            return "Switching to Computed Types will take you to the Asset Type Review stage."
        }
        return ""
    }
}
